type UseMultipleFloorsParameters = {
    blockClass: string;
    blockElements: NodeListOf<HTMLElement>;
    language: string;
    site: string;
};

type TranslationKey = 'showFloor1' | 'showFloor2';

const floorToggleTranslations: Record<string, Record<TranslationKey, string>> = {
    en: {
        showFloor1: 'Show Floor 1',
        showFloor2: 'Show Floor 2',
    },
    sv: {
        showFloor1: 'Visa Plan 1',
        showFloor2: 'Visa Plan 2',
    },
};

// TODO: Handle floors within the Interactive Map Block
// This function takes two InteractiveMapBlock and modifies them as two floors
// in a wrapper. A button is created to toggle between these two floors. If a theme
// requires more than two floors we will have to refactor this functionality.
const useMultipleFloors = ({
    blockClass,
    blockElements,
    language,
    site,
}: UseMultipleFloorsParameters): void => {
    const desktopBreakpoint = window.matchMedia('(min-width: 1320px)');
    const translatedText = floorToggleTranslations[language] ?? floorToggleTranslations.sv;

    if (blockElements.length < 2) {
        return;
    }

    const wrapFloorBlocks = (): HTMLDivElement => {
        const originalParentElement = blockElements[0].parentElement as HTMLDivElement | null;

        if (!originalParentElement) {
            throw new Error('Interactive map floors could not be wrapped because the parent element was missing.');
        }

        const wrapperSectionElement = document.createElement('div');
        wrapperSectionElement.classList.add('section', `section--bg-color-as-page-${site}`, blockClass);

        const wrapperContainerElement = document.createElement('div');
        wrapperContainerElement.classList.add('section__container', `${blockClass}__container`, `${blockClass}__container--multiple-floors`);

        wrapperSectionElement.appendChild(wrapperContainerElement);
        originalParentElement.insertBefore(wrapperSectionElement, blockElements[0]);

        blockElements.forEach((blockElement, floorIndex) => {
            const blockContainerElement = blockElement.querySelector<HTMLElement>(`.${blockClass}__container`);

            if (!blockContainerElement) {
                return;
            }

            blockContainerElement.classList.remove('section__container');
            blockElement.classList.remove('section', `section--bg-color-as-page-${site}`, blockClass);
            blockElement.classList.add(`${blockClass}__floor`);
            blockElement.id = `${blockClass}-floor-${floorIndex + 1}`;

            wrapperContainerElement.appendChild(blockElement);
        });

        return wrapperContainerElement;
    };

    const createToggleButton = (wrapperContainerElement: HTMLDivElement): HTMLButtonElement => {
        const toggleButtonElement = document.createElement('button');

        toggleButtonElement.type = 'button';
        toggleButtonElement.classList.add(`${blockClass}__toggle-floor-button`, 'button');
        toggleButtonElement.setAttribute('aria-controls', `${blockClass}-floor-1 ${blockClass}-floor-2`);
        toggleButtonElement.setAttribute('aria-pressed', 'false');
        toggleButtonElement.innerHTML = `
            <span class="button__icon icon icon--layers"></span>
            <span class="button__text">${translatedText.showFloor2}</span>
        `;

        wrapperContainerElement.prepend(toggleButtonElement);

        return toggleButtonElement;
    };

    const setActiveFloor = (toggleButtonElement: HTMLButtonElement, activeFloorIndex: number): void => {
        const toggleButtonTextElement = toggleButtonElement.querySelector<HTMLSpanElement>('.button__text');

        blockElements.forEach((blockElement, floorIndex) => {
            blockElement.classList.toggle(`${blockClass}__floor--active`, floorIndex === activeFloorIndex);
        });

        toggleButtonElement.setAttribute('aria-pressed', String(activeFloorIndex === 1));

        if (toggleButtonTextElement) {
            toggleButtonTextElement.innerText = activeFloorIndex === 0
                ? translatedText.showFloor2
                : translatedText.showFloor1;
        }
    };

    const wrapperContainerElement = wrapFloorBlocks();
    const toggleButtonElement = createToggleButton(wrapperContainerElement);
    let activeFloorIndex = 0;

    setActiveFloor(toggleButtonElement, activeFloorIndex);

    toggleButtonElement.addEventListener('click', () => {
        if (desktopBreakpoint.matches) {
            return;
        }

        activeFloorIndex = activeFloorIndex === 0 ? 1 : 0;
        setActiveFloor(toggleButtonElement, activeFloorIndex);
    });
};

export default useMultipleFloors;
